import { useContext, useState } from 'react';
import DetailsCard from '../DetailsCard';
import Image from 'next/image';
import { BsArrowRight } from 'react-icons/bs';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { getDeviceType } from '../../../../../utils/functions/getDeviceType';
import ReactMarkdown from 'react-markdown';
import { getImgUrl } from '@utils/functions/getImgUrl';
import { EventContext } from 'pages/events';

export const AllMenuItems = () => {
  const {
    faq,
    overview,
    instructor,
    isWorkshop,
    aboutProgram,
    slug,
    rewardImage,
    rewardText,
    certificate,
  } = useContext(EventContext);

  return (
    <>
      {overview && <LearnInfo />}
      {instructor && <Mentors />}
      <MasterClass />
      {isWorkshop && <ScholarShip />}
      {rewardImage?.data && rewardText && <Rewards />}
      <Prerequisites />
      {certificate?.data && <Certification />}
      {faq?.length && <Faq />}
      {aboutProgram && <AboutProgram />}
      <PastEvents />
    </>
  );
};

const ScholarShip = () => {
  const { scholarshipText, scholarshipImage } = useContext(EventContext);

  return (
    <DetailsCard className="bg-blue-light-1" title="Grab the scholarship">
      <section className="flex flex-wrap items-center justify-center gap-6 md:flex-nowrap md:justify-between">
        <p className="text-center text-base leading-8">{scholarshipText}</p>
        <img src={getImgUrl(scholarshipImage)} alt="scholarship" className="h-52" />
      </section>
    </DetailsCard>
  );
};

export const LearnInfo = () => {
  const { overview, isWorkshop } = useContext(EventContext);
  const title = isWorkshop ? 'Why you should attend this workshop' : 'What you will learn';
  return (
    <DetailsCard className="bg-blue-light-1" title={title}>
      <ReactMarkdown
        components={{
          h1: (props) => <h1 {...props} className="mb-4  font-satoshi-bold text-xl" />,
          p: (props) => (
            <p {...props} className="mb-8  text-justify font-satoshi-medium text-base lg:w-[85%]" />
          ),
          a: ({ node, href, children, ...props }) => (
            <a {...props} href={href} target="_blank">
              <a className="font-satoshi-medium text-base  text-blue hover:underline">{children}</a>
            </a>
          ),
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-inside text-base tracking-normal" />
          ),
          strong: ({ node, ...props }) => (
            <span {...props} className="font-satoshi-bold text-base font-bold" />
          ),
          li: ({ node, ...props }) => (
            <div className="item-start flex">
              <div className="w-4">
                <div className="mt-2 mr-2 h-2 w-2 rounded-full bg-gray-dark/90 md:mt-1.5"></div>
              </div>
              <li {...props} className="listing mb-4 font-satoshi-medium text-sm tracking-wide" />
            </div>
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="mb-4 list-inside list-decimal font-satoshi-medium" />
          ),
        }}
      >
        {overview}
      </ReactMarkdown>
    </DetailsCard>
  );
};

export const AboutProgram = () => {
  const { aboutProgram } = useContext(EventContext);

  return (
    <DetailsCard className="bg-blue-light-1" title="About AlmaBetter’s Program ">
      <ReactMarkdown
        components={{
          h1: (props) => <h1 {...props} className="mb-4  font-satoshi-bold text-base" />,
          p: (props) => (
            <p {...props} className="mb-8  text-justify font-satoshi-medium text-sm lg:w-[85%]" />
          ),
          a: ({ node, href, children, ...props }) => (
            <a {...props} href={href} target="_blank">
              <a className="font-satoshi-medium text-base  text-blue hover:underline">{children}</a>
            </a>
          ),
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-inside text-base tracking-normal" />
          ),
          strong: ({ node, ...props }) => (
            <span {...props} className="font-satoshi-bold text-base font-bold" />
          ),
          li: ({ node, ...props }) => (
            <div className="item-start flex">
              <div className="w-4">
                <div className="mt-2 mr-2 h-2 w-2 rounded-full bg-gray-dark/90 md:mt-1.5"></div>
              </div>
              <li {...props} className="listing mb-4 font-satoshi-medium text-sm tracking-wide" />
            </div>
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="mb-4 list-inside list-decimal font-satoshi-medium" />
          ),
        }}
      >
        {aboutProgram}
      </ReactMarkdown>
    </DetailsCard>
  );
};

export const Prerequisites = () => {
  const { preRequisites } = useContext(EventContext);
  return (
    <DetailsCard className="bg-green-1" title="Prerequisites">
      <section>
        <p className="font-satoshi-medium text-base leading-8">
          {preRequisites ??
            'There is no prerequisite for this event. Anyone interested in Data Science can join this Masterclass.'}
        </p>
      </section>
    </DetailsCard>
  );
};

export const Faq = () => {
  const { faq } = useContext(EventContext);
  const [isOpen, setIsOpen] = useState(0);
  return (
    <DetailsCard className="bg-blue-light-1" title="Frequently asked questions">
      <section className="grid select-none grid-cols-1 gap-5">
        {faq?.map(({ question, answer }, index) => (
          <details
            key={index}
            open={!index}
            onClick={() => {
              setIsOpen(index);
            }}
            className="w-12/12 rounded-md border border-solid border-gray-light p-4"
          >
            <summary>
              <div
                className={`text-[18px] leading-7 ${
                  isOpen === index && 'text-red'
                }  w-10/12 font-satoshi-medium`}
              >
                <span>{question}</span>
              </div>
            </summary>{' '}
            <p className="mt-5 w-10/12 py-5 font-satoshi-medium text-[16px] leading-6 text-gray-dark">
              {answer}
            </p>
          </details>
        ))}
      </section>
    </DetailsCard>
  );
};

export const Certification = () => {
  const { certificate, slug } = useContext(EventContext);

  return (
    <DetailsCard className="bg-orange/10" title="Certification">
      <section className="grid grid-cols-1 justify-items-center gap-5 text-center">
        <p className="text-sm md:w-10/12 md:text-base">
          Earn industry recognised certifications after completing the masterclass and add to your
          resume and LinkedIn profile.
        </p>
        <img
          src={certificate?.data?.attributes?.url ?? `/assets/events/certificate.png`}
          height={550}
          width={800}
          alt="certificate"
        />
      </section>
    </DetailsCard>
  );
};

export const MasterClass = () => {
  const { masterClassIsFor, isWorkshop, workshopIsFor } = useContext(EventContext);
  if (isWorkshop) {
    return (
      <DetailsCard className="bg-green-1" title="This Workshop is for">
        <section className="grid gap-6 md:grid-cols-3">
          {workshopIsFor?.map((data, index) => (
            <div key={index} className="flex flex-col items-center md:justify-center">
              <img src={getImgUrl(data.Image)} alt="image" className="mb-2 h-28" />
              <p className="mx-auto w-11/12 text-center text-sm">{data.title}</p>
            </div>
          ))}
        </section>
      </DetailsCard>
    );
  }

  return (
    <DetailsCard className="bg-green-1" title="This Masterclass is for">
      <section className="grid gap-6 md:grid-cols-3">
        {masterClassIsFor?.map((data, index) => (
          <div key={index} className="flex items-center gap-x-[16px] md:justify-center">
            <h5 className="w-12 text-left font-satoshi-bold text-5xl text-gray-dark/80 md:text-7xl">
              {index + 1}
            </h5>
            <p className="mx-auto w-10/12 font-satoshi-medium text-[16px] leading-5">
              {data.condition}
            </p>
          </div>
        ))}
      </section>
    </DetailsCard>
  );
};

export const Mentors = () => {
  const { instructor } = useContext(EventContext);
  const isGroup = instructor?.data?.filter((data) => data?.attributes?.Name === 'Group') || [];

  return (
    <DetailsCard className="bg-blue-light-1" title="Meet Your Instructors">
      <section className="flex flex-col items-center justify-center gap-4 sm:flex-row">
        {instructor?.data?.map((item) => {
          const { LinkedInUrl, Name, aboutCoach, Designation, DisplayPicture, CompanyImage } =
            item?.attributes || {};
          if (Name === 'Group') return null;
          return (
            <div
              key={item.id}
              className={`relative mx-3 flex w-10/12 flex-col items-center gap-y-2 rounded-md bg-blue-light-1 py-4 text-center md:mx-0 lg:mb-auto`}
            >
              <img
                height={200}
                width={200}
                alt="instructor"
                src={getImgUrl(DisplayPicture)}
                className="h-40 object-contain"
              />
              <h5 className="font-satoshi-medium text-base font-medium ">{Name}</h5>
              <p className="text-blue-500 w-10/12 font-satoshi-regular text-sm">{Designation}</p>
              <Image
                height={30}
                width={100}
                objectFit="contain"
                alt="almabetter"
                className="w-18 h-6 object-contain"
                src={getImgUrl(CompanyImage)}
              />
              <div className="absolute bottom-5 right-5 h-4">
                <a href={LinkedInUrl} target="_blank" rel="noreferrer">
                  <Image
                    loading="lazy"
                    height={20}
                    width={20}
                    alt="linked-in"
                    src="/assets/events/linkedin-mini.png"
                  />
                </a>
              </div>
            </div>
          );
        })}
      </section>
      {isGroup.length || instructor?.data?.[0]?.attributes?.aboutCoach ? (
        <p
          style={{
            fontSize: '0.95rem',
            lineHeight: '1.8rem',
          }}
          className="mx-auto mt-6  text-center font-satoshi-medium text-base"
        >
          {isGroup?.[0]?.attributes?.aboutCoach ?? instructor?.data?.[0]?.attributes?.aboutCoach}
        </p>
      ) : null}
    </DetailsCard>
  );
};

export const Rewards = () => {
  const { rewardImage, rewardText } = useContext(EventContext);
  const data = [
    {
      title: 'register',
      content: 'Register for the AlmaBetter Masterclass',
    },
    { title: 'Participate', content: 'Answer the quiz questions during the LIVE event' },
    { title: 'Win', content: 'Win exciting gift hampers' },
  ];
  return (
    <DetailsCard className="bg-green-1" title="Perks and benefits">
      <section className="grid items-center justify-center gap-5 md:grid-cols-2">
        <p className="text-lg leading-8 md:w-10/12">{rewardText}</p>
        <img
          src={getImgUrl(rewardImage)}
          height={350}
          width={350}
          alt="rewards"
          loading="lazy"
          className="object-contain"
        />
      </section>
      <section className="mt-4 md:mt-0">
        <p className="text-lg font-extrabold md:w-5/12">
          You are just 2 steps away from winning the prize!
        </p>
        <section className="flex flex-wrap items-center md:flex-nowrap">
          {data.map(({ title, content }, index) => (
            <div key={title} className="relative mt-8 flex">
              <p className="absolute top-0 left-0 -z-10 flex h-12 w-12 items-center justify-center rounded-full bg-blue-light-1 text-center font-satoshi-medium text-3xl font-semibold">
                {index + 1}
              </p>
              <div className="mt-4 ml-9">
                <p className="mb-3 text-base font-extrabold">{title}</p>
                <p className="w-11/12 text-sm font-extralight text-gray-dark">{content}</p>
              </div>
            </div>
          ))}
        </section>
      </section>
    </DetailsCard>
  );
};

export const PastEvents = () => {
  const {
    pastEvents: { data },
  } = useContext(EventContext);
  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 1,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 1,
    },
    tablet: {
      breakpoint: { max: 1024, min: 679 },
      items: 1,
    },
    mobile: {
      breakpoint: { max: 678, min: 0 },
      items: 1,
    },
  };
  const deviceType = getDeviceType();

  return (
    <DetailsCard className="bg-blue-light-1" title="Past Events">
      <section className="hidden snap-x  gap-x-6 overflow-x-auto md:flex">
        {data?.map(({ attributes }, index) => (
          <section
            key={index}
            className="min-w-[20rem] flex-1 snap-center border border-gray-light"
          >
            <img
              src={getImgUrl(attributes?.eventImage)}
              height={200}
              width={400}
              className="object-contain"
              alt="past events"
            />
            <p className="px-6 py-5 text-lg font-extrabold">{attributes?.eventTitle}</p>
            <a href={attributes?.registerLink} target="_blank">
              <button className="flex w-full items-center justify-center border-t border-gray-light p-4 text-center font-satoshi-medium font-medium  text-red">
                View info
                <BsArrowRight className="ml-2" />
              </button>
            </a>
          </section>
        ))}
      </section>
      <div className="mx-auto flex  w-[90vw] items-center md:hidden">
        <Carousel
          swipeable={true}
          draggable={true}
          responsive={responsive}
          arrows={true}
          ssr={true} // means to render carousel on server-side.
          partialVisible={true}
          itemClass="slider-crousel"
          keyBoardControl={true}
          containerClass="carousel-container"
          removeArrowOnDeviceType={['tablet', 'mobile']}
          deviceType={deviceType}
          dotListClass="hire-dots"
          showDots={true}
          autoPlay={false}
        >
          {data.map(({ attributes }, index) => (
            <section key={index} className="w-full flex-1 border border-gray-light">
              <img
                src={getImgUrl(attributes?.eventImage)}
                height={200}
                width={400}
                className="object-contain"
                alt="past events"
              />
              <p className="px-6 py-5 text-lg font-extrabold">{attributes?.eventTitle}</p>
              <a href={attributes?.registerLink} target="_blank">
                <button className="flex w-full items-center justify-center border-t border-gray-light p-4 text-center font-satoshi-medium font-medium  text-red">
                  View info
                  <BsArrowRight className="ml-2" />
                </button>
              </a>
            </section>
          ))}
        </Carousel>
      </div>
    </DetailsCard>
  );
};
